import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import TradingActionBoard from './TradingActionBoard';
import IstClock from './IstClock';
import { setChargeProduct } from './equity-delivery-charges';
import './NormalTrading.css';
import './NormalTradingV1.css';

const API = '/api/best-performer';
const POLL_MS = 2000;

export default function BestPerformer() {
  const [board, setBoard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const inFlight = useRef(false);

  const load = useCallback(async (warm = false) => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const res = await axios.get(`${API}/live-board`, {
        timeout: 20000,
        params: warm ? { warm: 1 } : undefined
      });
      setBoard(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Board load failed');
    } finally {
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    // Match backend withIntradayCharges — keep card fee/net on MIS math
    setChargeProduct('intraday');
    load(true);
    const t = setInterval(() => load(false), POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const wipe = useCallback(async () => {
    if (!window.confirm('Wipe Best Performer desk?\n\nClears BUY / HOLD / SELL / Sold Today.\nResets the scheduled capital · mandatory 20 shares per stock.')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/wipe`, {}, { timeout: 15000 });
      await load(true);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Wipe failed');
    } finally {
      setBusy(false);
    }
  }, [load]);

  const lists = board?.actionLists || {};
  const buy = lists.buy || [];
  const hold = lists.hold || [];
  const sell = lists.sell || [];
  const soldToday = lists.soldToday || [];
  const profitBands = board?.profitBands || [];
  const profitLabel = board?.profitLabel === 'tiered'
    ? 'tiered ₹1.50–₹3.10'
    : (board?.profitLabel || 'tiered');
  const profitRs = Number(board?.profitRs) || 0.5;
  const cap = board?.capital || {};
  const basket = board?.basket || {};
  const settings = board?.settings || {};
  const trailingArmedCount = [...buy, ...hold].filter((item) => item.trailingArmed).length;
  const capitalAmount = Number(cap.dailyTradingCapital || board?.settings?.capitalAmount || 200000);
  const blockedStocks = board?.blockedBuyStocks || board?.settings?.blockedBuyStocks || [];

  const activeStep = useMemo(() => {
    // TradingActionBoard: 1=BUY, 2=SELL, 3=HOLD
    if (sell.length) return 2;
    if (hold.length) return 3;
    if (buy.length) return 1;
    return 1;
  }, [sell.length, hold.length, buy.length]);

  return (
    <div className="nt-desktop nt-v1 nt-no-sidebar best-performer-screen">
      <main className="nt-main">
        <header className="nt-header nt-header-compact">
          <div>
            <h1>Best Performer</h1>
            <p className="nt-subtitle">
              ₹{(capitalAmount / 100000).toFixed(0)}L capital · <strong>20 shares mandatory</strong> · fee-aware profit trailing · uptrend charts only
            </p>
            <p className="nt-subtitle" style={{ marginTop: 4 }}>
              New BUY → 1-minute check · keep only if estimated <strong>net profit is plus</strong> (black BEST OF BEST card) · minus/flat net is removed from BUY
            </p>
            {blockedStocks.length > 0 && (
              <p className="nt-subtitle" style={{ marginTop: 4 }}>
                High-charge BUY block: <strong>{blockedStocks.join(' · ')}</strong>
              </p>
            )}
            {profitBands.length > 0 && (
              <p className="nt-subtitle" style={{ marginTop: 4, fontSize: '0.78rem', opacity: 0.9 }}>
                {profitBands.map((b) => b.label).join(' · ')}
              </p>
            )}
          </div>
          <div className="nt-v1-master-clock">
            <IstClock />
          </div>
          <div className="nt-v1-header-links" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <Link to="/NormalTrading" className="nt-v1-btn">← V1 board</Link>
            <Link to="/Report?screen=best-performer" className="nt-v1-btn">Report</Link>
            <button type="button" className="nt-v1-btn primary" onClick={() => load(true)} disabled={busy}>
              Refresh
            </button>
            <button type="button" className="nt-v1-btn danger" onClick={wipe} disabled={busy}>
              Wipe desk
            </button>
          </div>
        </header>

        {error && <div className="nt-v1-master-error">{error}</div>}

        <div className="nt-v1-status-strip" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
          <span>Status: <strong>{board?.status || '…'}</strong></span>
          <span>Wallet: <strong>₹{Number(cap.walletTotal ?? capitalAmount).toLocaleString('en-IN')}</strong></span>
          <span>Free: <strong>₹{Number(cap.capitalAvailable || 0).toLocaleString('en-IN')}</strong></span>
          <span>In use: <strong>₹{Number(cap.capitalInUse || 0).toLocaleString('en-IN')}</strong></span>
          <span>Gross: <strong>₹{Number(board?.dailyGrossPnl || 0).toFixed(2)}</strong></span>
          <span>Charges: <strong>₹{Number(board?.dailyCharges || 0).toFixed(2)}</strong></span>
          <span>Day net: <strong>₹{Number(board?.dailyPnl || 0).toFixed(2)}</strong></span>
          <span>Open: <strong>{basket.slotsUsed || 0}</strong> names</span>
          <span>Trailing armed: <strong>{trailingArmedCount}</strong></span>
          <span style={{ opacity: 0.85 }}>{board?.lastAction || ''}</span>
        </div>

        <section style={{ marginBottom: 16 }}>
          <h2 style={{ margin: '0 0 10px', fontSize: '1rem' }}>Improvement plan · active</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 8 }}>
            {[
              ['Fee-aware exits', `Tier target or charge break-even + ₹${Number(settings.slippageBufferRsPerShare || 0.1).toFixed(2)}/share buffer · desired net ₹${Number(settings.desiredNetProfitRs || 10).toFixed(0)}`],
              ['Black-card net bank', `If attached-table ₹/sh not hit yet, black card with estimated net > ₹${Number(settings.blackCardNetFallbackRs || 4).toFixed(0)} → SELL → Sold Today.`],
              ['Correct report accounting', 'Gross P&L, brokerage, taxes, total charges and true net P&L are stored separately.'],
              ['Immediate execution', `Sell price is captured immediately; executed card remains in SELL for ${Number(settings.sellTabAutoSoldSec || 10)} seconds.`],
              ['Trailing profit lock', `Fee-aware target hit → peak follows price · stop floor never below target · pullback or ${Number(settings.trailingMaxHoldSec || 120)}s timeout executes profit.`],
              ['Churn control', `No per-stock daily cap — pick by today's best performers · ${Number(settings.provisionalCancelCooldownMinutes || 2)}-minute cancel cooldown · ${Number(settings.reentryCooldownMinutes || 2)}-minute after Sold Today.`],
              ['Adaptive eligibility', `${Number(settings.adaptiveLookbackDays || 5)}-day rolling net/trade and charge-drag gate with minimum ${Number(settings.adaptiveMinSampleTrades || 5)} trades.`],
              ['Time quality gate', `Trend strength ≥ ${Number(settings.currentRequiredTrendPct || 0.25).toFixed(2)}% now; softer only during profitable 10am and 1pm windows.`]
            ].map(([title, text]) => (
              <div
                key={title}
                style={{
                  border: '1px solid var(--nt-border, #d7dce2)',
                  borderRadius: 8,
                  padding: '10px 12px',
                  background: 'var(--nt-panel, rgba(255,255,255,0.03))'
                }}
              >
                <strong style={{ display: 'block', marginBottom: 4 }}>{title}</strong>
                <span style={{ fontSize: '0.78rem', opacity: 0.82, lineHeight: 1.4 }}>{text}</span>
              </div>
            ))}
          </div>
        </section>

        <TradingActionBoard
          buy={buy}
          hold={hold}
          sell={sell}
          sellActive={sell}
          sellHistory={soldToday}
          bestPerformersToday={null}
          stopLossToday={[]}
          shortToday={[]}
          hideSecondaryTabs
          sellUrgentCount={sell.filter((s) => s.liveSellAlert || s.sellNow).length}
          sellTodayCount={soldToday.length}
          activeStep={activeStep}
          profitLabel={profitLabel}
          profitRs={profitRs}
          holdPartialMin={profitRs}
          holdPartialMax={Math.max(profitRs, 2.3)}
          holdMinutes={10}
          stockQty={20}
          slotsMax={basket.slotsMax || 999}
          volatileMode
          quantSlotCount={basket.slotsUsed || 0}
          liveStatus={board?.marketOpen ? 'live' : 'closed'}
          fullyAutomated
          slotsFree={basket.slotsFree}
          holdSlotCount={hold.length}
        />
      </main>
    </div>
  );
}
